import { Button, Table } from 'antd';
import { ListChecks, ShieldAlert } from 'lucide-react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { Link } from 'react-router';
import { useTripEvents } from '../hooks/use-trip-events';
import { TRIP_EVENT_PARAM } from '../hooks/use-trip-focus';
import { tripPaths } from '../trips.paths';
import { tripStatusLabel } from '../policies/trip-status-label.policy';
import type { TripLogEntry } from '../trips.api';

/** The raw event log: what each source said and when, with the device's clock beside the server's (the two clocks). */
export function TripEventsTab({ tripId }: { tripId: string }) {
  const { state, retry } = useTripEvents(tripId);

  if (state.status === 'loading') return <TableSkeleton label="Loading events" rows={5} />;
  if (state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load the events" description="Something went wrong reading this trip's events.">
        <Button onClick={retry}>Try again</Button>
      </EmptyState>
    );
  }
  if (state.events.length === 0) {
    return <EmptyState icon={ListChecks} title="No events yet" description="Events appear here as the trip is assigned, dispatched and driven." />;
  }

  return (
    <Table<TripLogEntry>
      rowKey="id"
      dataSource={state.events}
      pagination={false}
      scroll={{ x: 'max-content' }}
      columns={[
        { title: 'Event', key: 'event', render: (_, entry) => tripStatusLabel(entry.eventType) + (entry.correctsEventId ? ' (correction)' : '') },
        { title: 'From', key: 'source', render: (_, entry) => tripStatusLabel(entry.source) },
        { title: 'Device time', key: 'device', render: (_, entry) => new Date(entry.deviceTime).toLocaleString() },
        { title: 'Server time', key: 'server', render: (_, entry) => new Date(entry.serverTime).toLocaleString() },
        { title: 'Map', key: 'map', render: (_, entry) => <Link to={`${tripPaths.trip(tripId)}?${TRIP_EVENT_PARAM}=${entry.id}`}>Show on map</Link> },
      ]}
    />
  );
}
